import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Linkedin, FileText, Globe, Folder, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';

interface ProfessionalLinksCardProps {
  linkedinUrl?: string | null;
  portfolioUrl?: string | null;
  resumeUrl?: string | null;
  websiteUrl?: string | null;
  isHidden?: boolean;
  onRequestInfo?: () => void;
  className?: string;
}

export const ProfessionalLinksCard: React.FC<ProfessionalLinksCardProps> = ({
  linkedinUrl,
  portfolioUrl,
  resumeUrl,
  websiteUrl,
  isHidden = false,
  onRequestInfo,
  className
}) => {
  const hasLinks = linkedinUrl || portfolioUrl || resumeUrl || websiteUrl;
  
  if (!hasLinks) return null;

  const links = [
    { url: linkedinUrl, label: 'LinkedIn', icon: <Linkedin className="w-4 h-4" />, color: 'bg-[#0077B5]/10 text-[#0077B5] hover:bg-[#0077B5]/20' },
    { url: portfolioUrl, label: 'Portfolio', icon: <Folder className="w-4 h-4" />, color: 'bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-400' },
    { url: resumeUrl, label: 'Resume/CV', icon: <FileText className="w-4 h-4" />, color: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
    { url: websiteUrl, label: 'Website', icon: <Globe className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400' },
  ].filter(link => link.url);

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ExternalLink className="w-4 h-4 text-primary" />
          Professional Links
          {isHidden && <Lock className="w-3 h-3 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isHidden ? (
          <BlurredPrivateData
            placeholderContent={
              <div className="grid grid-cols-2 gap-2">
                {links.map((link, idx) => (
                  <div key={idx} className="h-10 bg-muted rounded-lg" />
                ))}
              </div>
            }
            fieldName="Professional Links"
            onRequestInfo={onRequestInfo}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {links.map((link, idx) => (
              <Button
                key={idx}
                variant="ghost"
                size="sm"
                className={cn("justify-start gap-2 h-10", link.color)}
                asChild
              >
                <a href={link.url!} target="_blank" rel="noopener noreferrer">
                  {link.icon}
                  <span className="text-xs font-medium">{link.label}</span>
                  <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                </a>
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfessionalLinksCard;
